import { Component, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { CheckoutService } from '@app/core/services/checkout.service';
import { LatestPurchasesService } from '@app/core/services/latest-purchases.service';
import { SimpleOrder } from '@app/shared/interfaces/simple-order.interface';
import { ToastController, IonSlides } from '@ionic/angular';
import { Observable, Subject } from 'rxjs';
import { first, takeUntil, throttleTime } from 'rxjs/operators';

@Component({
  selector: 'app-latest-purchases',
  templateUrl: './latest-purchases.component.html',
  styleUrls: ['./latest-purchases.component.scss'],
})
export class LatestPurchasesComponent implements OnInit, OnDestroy {

  @Input()
  isMobile: boolean;

  @Input()
  isAuthenticated: Observable<boolean>;

  @ViewChild(IonSlides) slides: IonSlides;
  
  activeIndex = 0;
  latestPurchases: SimpleOrder[] = [];
  slideOptions = {
    slidesPerView: 2,
    centeredSlides: true,
    loop: true,
    runCallbacksOnInit: false,
  }

  private selectOrderSubject = new Subject<SimpleOrder>();
  private onDestroy = new Subject();

  constructor(
    private latestPurchaseService: LatestPurchasesService, 
    private checkoutService: CheckoutService,
    private router: Router,
    public toastController: ToastController
    ) { }

  ngOnDestroy() {
    this.onDestroy.next(null);
  }

  ngOnInit() {
    this.latestPurchaseService.getLatestPurchases().asObservable()
    .pipe(takeUntil(this.onDestroy))
    .subscribe(async latestPurchases => {
      this.latestPurchases = latestPurchases;

      if (!!this.slides) {
        const swiper = await this.slides.getSwiper();
        swiper.update();
      }
    });

    this.selectOrderSubject.pipe(
      takeUntil(this.onDestroy),
      throttleTime(1500, undefined, { leading: true, trailing: false }) // Throttle to prevent multiple clicks in a row
    ).subscribe(order => {
      this.repeatPurchase(order);
    });
  }

  async presentNotLoggedInErrorToast() {
    const toast = await this.toastController.create({
      message: 'Du måste vara inloggad för att köpa denna typ av biljett',
      color: 'danger',
      position: 'top',
      duration: 5000
    });
    toast.present();
  }
  
  async repeatPurchase(order: SimpleOrder) {
    this.latestPurchaseService.updateSimpleOrderItemsWithCurrentPrices(order.simpleOrderItems).pipe(first()).subscribe(orderItems => {
      this.checkoutService.setOrderItems(order.simpleOrderItems);
      this.router.navigate(['checkout']);
    });
  }

  onOrderSelect(order: SimpleOrder) {
    this.selectOrderSubject.next(order);
  }

  onSlideChange() {
    this.slides.getActiveIndex().then(index => {
      this.activeIndex = this.getActualIndex(index);
    });
  }

  onSlideTap(event: CustomEvent) {
    if (event.detail.type === 'touchend') {
      this.slides.getSwiper().then(swiper => {
        const selectedIndex = this.getActualIndex(swiper.clickedIndex);
        this.selectOrderSubject.next(this.latestPurchases[selectedIndex]);
      });
    }
  }

  getActualIndex(sliderIndex: number): number {
    let actualIndex = sliderIndex;

    if (sliderIndex < 2) {
      actualIndex = this.latestPurchases.length - (2 - sliderIndex);
    }
    else {
      //Slide index starts at 2, so 2 = 0
      actualIndex = sliderIndex - 2;

      if (actualIndex > this.latestPurchases.length - 1) {
        actualIndex = this.getActualIndex(sliderIndex - this.latestPurchases.length);
      }
    }

    return actualIndex;
  }

  removeOrder(event: MouseEvent, order: SimpleOrder) {
    this.latestPurchaseService.removeOrder(order);
    event.stopPropagation();
  }

  trimVehicleTypeDescription(vehicleTypeDescription: string): string {
    return vehicleTypeDescription.replace(' 0-6m', '');
  }
}
